import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import { useGoBack } from "../../../hooks/useGoBack";

import {
  createEntryApi,
  createRecurrenceApi,
  getEntryApi,
  listAccountsApi,
  listCategoriesApi,
  listStoresApi,
  updateEntryApi,
} from "../api/financeiroApi";
import { isDpSource,
  FinAccount,
  FinEntryDetail,
  FinEntryType,
  FinFrequency,
  FinStore,
  FREQUENCY_LABELS,
  KIND_FOR_TYPE,
} from "../api/finTypes";
import CounterpartyPicker from "../components/CounterpartyPicker";
import CostCenterCascade from "../components/CostCenterCascade";
import AllocationEditor, { AllocationRow, allocationErrors, allocationPayload } from "../components/AllocationEditor";
import { useCostCenters } from "../utils/costCenters";
import { FINANCEIRO_PAGE, FIN_ENTRIES_PAGE, FIN_ENTRY_PAGE } from "../routes";
import { formatCurrency } from "../../dp/utils/format";
import {
  CategoryOption,
  flattenCategories,
  parseMoneyInput,
  previewInstallments,
  todayYmd,
  useFinPermissions,
} from "../utils/fin";
import "../../dp/pages/Dp.css";
import "../../dp/pages/DpEco.css";
import "./Financeiro.css";

type Mode = "unico" | "parcelado" | "repetir";

const FREQUENCIES = Object.keys(FREQUENCY_LABELS) as FinFrequency[];

/**
 * Lançar ou editar uma conta a pagar/receber. Na criação, três formas: único,
 * parcelado (N títulos de uma vez) ou repetir (gera os títulos da repetição).
 * Na edição, um título só; valor bloqueado quando já tem baixa.
 */
export default function EntryFormPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [search] = useSearchParams();
  const { token, canWrite } = useFinPermissions();
  const isEdit = Boolean(id);

  const initialType: FinEntryType = search.get("tipo") === "RECEBER" ? "RECEBER" : "PAGAR";
  const [entry, setEntry] = useState<FinEntryDetail | null>(null);
  const [type, setType] = useState<FinEntryType>(initialType);

  const goBack = useGoBack(isEdit && id ? FIN_ENTRY_PAGE(id) : FIN_ENTRIES_PAGE(initialType));

  const initialMode: Mode = search.get("forma") === "repetir" ? "repetir" : search.get("forma") === "parcelado" ? "parcelado" : "unico";
  const [mode, setMode] = useState<Mode>(initialMode);
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [competenceDate, setCompetenceDate] = useState(todayYmd());
  const [dueDate, setDueDate] = useState(todayYmd());
  const [categoryId, setCategoryId] = useState("");
  const [costCenterId, setCostCenterId] = useState("");
  /** Rateio entre centros: com ele ligado, o título não tem centro único nem loja. */
  const [allocating, setAllocating] = useState(false);
  const [allocations, setAllocations] = useState<AllocationRow[]>([]);
  const [storeId, setStoreId] = useState("");
  const [accountId, setAccountId] = useState("");
  const [counterparty, setCounterparty] = useState<{ id: string; name: string } | null>(null);
  const [notes, setNotes] = useState("");
  const [count, setCount] = useState("2");
  const [frequency, setFrequency] = useState<FinFrequency>("MENSAL");
  const [endDate, setEndDate] = useState("");

  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const { costCenters, groups } = useCostCenters(token);
  const [stores, setStores] = useState<FinStore[]>([]);
  const [accounts, setAccounts] = useState<FinAccount[]>([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);

  // Quem só lê não tem o que fazer aqui.
  useEffect(() => {
    if (!canWrite) navigate(isEdit && id ? FIN_ENTRY_PAGE(id) : FINANCEIRO_PAGE(), { replace: true });
  }, [canWrite, isEdit, id, navigate]);

  useEffect(() => {
    if (!token) return;
    listAccountsApi(token).then(setAccounts).catch(() => setAccounts([]));
    listStoresApi(token).then(setStores).catch(() => setStores([]));
  }, [token]);

  useEffect(() => {
    if (!token) return;
    listCategoriesApi(token, { kind: KIND_FOR_TYPE[type] })
      .then((tree) => setCategories(flattenCategories(tree)))
      .catch(() => setCategories([]));
  }, [token, type]);

  useEffect(() => {
    if (!token || !id) return;
    setLoading(true);
    getEntryApi(token, id)
      .then((e) => {
        setEntry(e);
        setType(e.type);
        setDescription(e.description);
        setAmount(e.amount);
        setCompetenceDate(e.competenceDate);
        setDueDate(e.dueDate);
        setCategoryId(e.category?.id ?? "");
        setCostCenterId(e.costCenter?.id ?? "");
        setAllocating(e.allocations.length > 0);
        setAllocations(
          e.allocations.map((a) => ({ costCenterId: a.costCenter.id, amount: Number(a.amount).toFixed(2).replace(".", ",") }))
        );
        setStoreId(e.store?.id ?? "");
        setAccountId(e.suggestedAccountId ?? "");
        setCounterparty(e.counterparty ? { id: e.counterparty.id, name: e.counterparty.name } : null);
        setNotes(e.notes ?? "");
      })
      .catch((error: Error) => toast.error(error.message))
      .finally(() => setLoading(false));
  }, [token, id]);

  const value = parseMoneyInput(amount);
  const installments = Math.min(120, Math.max(2, Number(count) || 2));
  const preview = useMemo(
    () => (mode === "parcelado" ? previewInstallments(value, installments) : []),
    [mode, value, installments]
  );
  // Título do DP (ADR 0029): o valor é a previsão do DP e muda só lá.
  const fromDp = Boolean(entry && isDpSource(entry.source));
  const amountLocked = Boolean(entry && (Number(entry.settledAmount) > 0 || fromDp));
  const isReceivable = type === "RECEBER";

  // Loja: as do centro de custo escolhido (quando ele tem lojas); sem centro, todas.
  const ccStoreIds = costCenters.find((c) => c.id === costCenterId)?.storeIds ?? [];
  const storeOptions = ccStoreIds.length ? stores.filter((s) => ccStoreIds.includes(s.id)) : stores;
  const changeCostCenter = (next: string) => {
    setCostCenterId(next);
    const allowed = costCenters.find((c) => c.id === next)?.storeIds ?? [];
    if (storeId && allowed.length && !allowed.includes(storeId)) setStoreId("");
  };

  const errors = useMemo(() => {
    const list: string[] = [];
    if (description.trim().length < 2) list.push("Informe a descrição.");
    if (!(value > 0)) list.push("Informe o valor.");
    if (!categoryId) list.push("Escolha a categoria.");
    if (!dueDate) list.push(mode === "repetir" ? "Informe o primeiro vencimento." : "Informe o vencimento.");
    if (mode !== "repetir" && !competenceDate) list.push("Informe a competência.");
    if (mode === "repetir" && endDate && endDate < dueDate) list.push("O fim da repetição é antes do início.");
    if (mode === "parcelado" && preview.some((p) => p <= 0)) list.push("Valor pequeno demais para tantas parcelas.");
    if (allocating) list.push(...allocationErrors(allocations, value));
    else if (!costCenterId) list.push("Informe o centro de custo.");
    return list;
  }, [description, value, categoryId, dueDate, competenceDate, mode, endDate, preview, allocating, allocations, costCenterId]);

  const toggleAllocating = () => {
    if (allocating) {
      setAllocating(false);
      setAllocations([]);
      return;
    }
    setAllocating(true);
    setStoreId("");
    setAllocations([
      { costCenterId, amount: "" },
      { costCenterId: "", amount: "" },
    ]);
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (errors.length) return toast.error(errors[0]);

    const common = {
      description: description.trim(),
      categoryId,
      ...(allocating
        ? { costCenterId: null, allocations: allocationPayload(allocations) }
        : { costCenterId: costCenterId || null, allocations: null }),
      counterpartyId: counterparty?.id ?? null,
    };

    setSaving(true);
    try {
      if (isEdit && id) {
        await updateEntryApi(token, id, {
          ...common,
          storeId: allocating ? null : storeId || null,
          suggestedAccountId: accountId || null,
          competenceDate,
          dueDate,
          notes: notes.trim() || null,
          ...(amountLocked ? {} : { amount: value }),
        });
        toast.success("Lançamento salvo.");
        navigate(FIN_ENTRY_PAGE(id), { replace: true });
        return;
      }

      if (mode === "repetir") {
        const rec = await createRecurrenceApi(token, {
          ...common,
          type,
          frequency,
          startDate: dueDate,
          endDate: endDate || null,
          amount: value,
          accountId: accountId || null,
        });
        toast.success(`Repetição criada: ${rec.generated} lançamentos gerados.`);
        navigate(FIN_ENTRIES_PAGE(type), { replace: true });
        return;
      }

      const created = await createEntryApi(token, {
        ...common,
        type,
        storeId: allocating ? null : storeId || null,
        suggestedAccountId: accountId || null,
        competenceDate,
        dueDate,
        amount: value,
        notes: notes.trim() || null,
        ...(mode === "parcelado" ? { installments: { count: installments, interval: frequency } } : {}),
      });
      toast.success(created.length > 1 ? `${created.length} parcelas lançadas.` : "Lançamento salvo.");
      navigate(FIN_ENTRY_PAGE(created[0].id), { replace: true });
    } catch (error: any) {
      toast.error(error?.message || "Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  const title = isEdit
    ? "Editar lançamento"
    : isReceivable
      ? "Nova conta a receber"
      : "Nova conta a pagar";

  return (
    <div className="dpePage">
      <main className="dpePage__main">
        <EcoPageHeader
          crumbs={[
            { label: "Financeiro", onClick: () => navigate(FINANCEIRO_PAGE()) },
            {
              label: isReceivable ? "Contas a receber" : "Contas a pagar",
              onClick: () => navigate(FIN_ENTRIES_PAGE(type)),
            },
            { label: title },
          ]}
          title={title}
          onBack={goBack}
          backLabel="Voltar"
        />

        {loading ? (
          <p className="ecoEmpty">Carregando...</p>
        ) : (
          <form onSubmit={save} className="finBox" noValidate>
            {!isEdit ? (
              <div className="dpFormGrid">
                <div className="dpFormGroup">
                  <label htmlFor="enType">Tipo *</label>
                  <select
                    id="enType"
                    className="dpSelect"
                    value={type}
                    onChange={(e) => {
                      setType(e.target.value as FinEntryType);
                      setCategoryId("");
                    }}
                    disabled={saving}
                  >
                    <option value="PAGAR">Conta a pagar (despesa)</option>
                    <option value="RECEBER">Conta a receber (receita)</option>
                  </select>
                </div>
                <div className="dpFormGroup">
                  <label htmlFor="enMode">Forma</label>
                  <select
                    id="enMode"
                    className="dpSelect"
                    value={mode}
                    onChange={(e) => setMode(e.target.value as Mode)}
                    disabled={saving}
                  >
                    <option value="unico">Lançamento único</option>
                    <option value="parcelado">Parcelado</option>
                    <option value="repetir">Repetir (aluguel, mensalidade)</option>
                  </select>
                </div>
              </div>
            ) : null}

            <div className="dpFormGroup">
              <label htmlFor="enDesc">Descrição *</label>
              <input
                id="enDesc"
                className="dpInput"
                maxLength={200}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={saving}
                placeholder={isReceivable ? "Ex.: Serviço prestado em outubro" : "Ex.: Aluguel do escritório"}
              />
            </div>

            <div className="dpFormGrid">
              <div className="dpFormGroup">
                <label htmlFor="enAmount">
                  {mode === "parcelado" ? "Valor total (R$) *" : mode === "repetir" ? "Valor de cada (R$) *" : "Valor (R$) *"}
                </label>
                <input
                  id="enAmount"
                  className="dpInput"
                  inputMode="decimal"
                  placeholder="0,00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  disabled={saving || amountLocked}
                />
                {fromDp ? (
                  <span className="dpFormHint">Gerado pelo DP: o valor muda só lá, ao recalcular.</span>
                ) : amountLocked ? (
                  <span className="dpFormHint">Já tem baixa: estorne as baixas para alterar o valor.</span>
                ) : null}
              </div>

              <div className="dpFormGroup">
                <label htmlFor="enCat">Categoria *</label>
                <select
                  id="enCat"
                  className="dpSelect"
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value)}
                  disabled={saving}
                >
                  <option value="">Escolha</option>
                  {categories
                    .filter((c) => c.isActive || c.id === categoryId)
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.label}
                      </option>
                    ))}
                </select>
              </div>

              <div className="dpFormGroup">
                <label htmlFor="enDue">{mode === "repetir" ? "Primeiro vencimento *" : mode === "parcelado" ? "Vencimento da 1ª parcela *" : "Vencimento *"}</label>
                <input
                  id="enDue"
                  type="date"
                  className="dpInput"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  disabled={saving}
                />
              </div>

              {mode !== "repetir" ? (
                <div className="dpFormGroup">
                  <label htmlFor="enComp">Competência *</label>
                  <input
                    id="enComp"
                    type="date"
                    className="dpInput"
                    value={competenceDate}
                    onChange={(e) => setCompetenceDate(e.target.value)}
                    disabled={saving}
                  />
                  <span className="dpFormHint">O mês a que a {isReceivable ? "receita" : "despesa"} pertence.</span>
                </div>
              ) : (
                <div className="dpFormGroup">
                  <label htmlFor="enEnd">Repetir até</label>
                  <input
                    id="enEnd"
                    type="date"
                    className="dpInput"
                    value={endDate}
                    min={dueDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    disabled={saving}
                  />
                  <span className="dpFormHint">Vazio: sem fim (gera 12 meses à frente; estenda depois em Repetições).</span>
                </div>
              )}
            </div>

            {mode === "parcelado" || mode === "repetir" ? (
              <div className="dpFormGrid">
                {mode === "parcelado" ? (
                  <div className="dpFormGroup">
                    <label htmlFor="enCount">Parcelas *</label>
                    <input
                      id="enCount"
                      type="number"
                      min={2}
                      max={120}
                      className="dpInput"
                      value={count}
                      onChange={(e) => setCount(e.target.value)}
                      disabled={saving}
                    />
                  </div>
                ) : null}
                <div className="dpFormGroup">
                  <label htmlFor="enInterval">{mode === "parcelado" ? "Intervalo" : "Frequência"}</label>
                  <select
                    id="enInterval"
                    className="dpSelect"
                    value={frequency}
                    onChange={(e) => setFrequency(e.target.value as FinFrequency)}
                    disabled={saving}
                  >
                    {FREQUENCIES.map((f) => (
                      <option key={f} value={f}>
                        {FREQUENCY_LABELS[f]}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ) : null}

            {preview.length > 0 ? (
              <div className="dpFormGroup">
                <span className="dpFormHint">Parcelas</span>
                <ul className="finInstallments">
                  {preview.map((p, i) => (
                    <li key={i}>
                      {i + 1}ª {formatCurrency(p)}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <div className="dpFormGrid">
              <div className="dpFormGroup">
                <label htmlFor="enCp">{isReceivable ? "Cliente" : "Fornecedor"}</label>
                <CounterpartyPicker
                  token={token}
                  id="enCp"
                  value={counterparty}
                  onChange={setCounterparty}
                  disabled={saving}
                  canCreate={canWrite}
                />
              </div>

              <div className="dpFormGroup">
                <label htmlFor="enAcc">{isReceivable ? "Receber na conta" : "Pagar pela conta"}</label>
                <select
                  id="enAcc"
                  className="dpSelect"
                  value={accountId}
                  onChange={(e) => setAccountId(e.target.value)}
                  disabled={saving}
                >
                  <option value="">Escolher na baixa</option>
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </div>

              {!allocating ? (
                <CostCenterCascade
                  idPrefix="en"
                  value={costCenterId}
                  onChange={changeCostCenter}
                  costCenters={costCenters}
                  groups={groups}
                  current={entry?.costCenter ?? null}
                  disabled={saving}
                  footer={
                    <button type="button" className="finLinkBtn" onClick={toggleAllocating} disabled={saving}>
                      Ratear entre centros
                    </button>
                  }
                />
              ) : null}

              {mode !== "repetir" && stores.length > 0 && !allocating ? (
                <div className="dpFormGroup">
                  <label htmlFor="enStore">Loja</label>
                  <select
                    id="enStore"
                    className="dpSelect"
                    value={storeId}
                    onChange={(e) => setStoreId(e.target.value)}
                    disabled={saving}
                  >
                    <option value="">{ccStoreIds.length === 1 ? "A do centro de custo" : "Nenhuma"}</option>
                    {storeOptions.map((st) => (
                      <option key={st.id} value={st.id}>
                        {st.name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
            </div>

            {allocating ? (
              <div className="dpFormGroup">
                <div className="finAlloc__head">
                  <span className="finAlloc__title">Rateio entre centros de custo *</span>
                  <button type="button" className="finLinkBtn" onClick={toggleAllocating} disabled={saving}>
                    Usar um centro só
                  </button>
                </div>
                <AllocationEditor
                  rows={allocations}
                  onChange={setAllocations}
                  total={value}
                  costCenters={costCenters}
                  groups={groups}
                  disabled={saving}
                />
                <span className="dpFormHint">
                  {mode === "parcelado"
                    ? "Rateie o valor total: cada parcela é dividida na mesma proporção."
                    : "Lançamento rateado não tem loja; a DRE mostra cada fatia no seu centro."}
                </span>
              </div>
            ) : null}

            {mode !== "repetir" ? (
              <div className="dpFormGroup">
                <label htmlFor="enNotes">Observações</label>
                <textarea
                  id="enNotes"
                  className="dpTextarea"
                  maxLength={1000}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  disabled={saving}
                />
              </div>
            ) : null}

            {isEdit && entry?.installmentNumber ? (
              <p className="dpNotice" style={{ margin: 0 }}>
                Esta é a parcela {entry.installmentNumber}/{entry.installmentCount}: a edição vale só para ela.
              </p>
            ) : null}
            {isEdit && entry?.recurrenceId ? (
              <p className="dpNotice" style={{ margin: 0 }}>
                Gerado por uma repetição: a edição vale só para este mês. Para mudar os próximos, edite a repetição em
                Cadastros → Repetições.
              </p>
            ) : null}

            <div className="finActions">
              <button type="button" className="ecoBtn" onClick={goBack} disabled={saving}>
                Cancelar
              </button>
              <button type="submit" className="ecoBtn ecoBtn--primary" disabled={saving}>
                {saving ? "Salvando..." : "Salvar"}
              </button>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}
